import * as React from 'react';
import {groupBy} from 'lodash';
import {Action, ActionType, Modal} from '>shared/components/modal/modal';
import {Paragraph} from '>shared/components/typography/baseTypography2';
import {Message, MessageType} from '>shared/components/form/message';
import {useModal} from '>shared/components/modal/useModal';
import {ModalDisplayType} from '>shared/components/modal/displayType';
import {generatePrefixed} from '>shared/lib/uniqueString';
import {FileInput} from '>shared/components/form/fileInput';
import {FileImportError} from '>generated/dvp.types';
import {Messages, FileInputContainer, FileInputSection} from './fileImport.styles';
import {dvpApi} from '>root/apis';

enum FileImportState {
  NEVER_SELECTED_FILE,
  FILE_CHOSEN_HAS_ERRORS,
  FILE_CHOSEN_SUCCESSFULLY,
  COMMIT_HAS_ERRORS,
}

function buildCancelAction(onCancel: () => void): Action {
  return {
    label: 'Cancel',
    type: ActionType.Secondary,
    inputType: 'button',
    onClick: onCancel,
    qaDataAttribute: 'cancel-button',
  };
}

function buildCommitAction(state: FileImportState, onCommit: () => void): Action {
  return {
    label: 'Upload',
    type: ActionType.Primary,
    inputType: 'button',
    disabled: state !== FileImportState.FILE_CHOSEN_SUCCESSFULLY,
    onClick: onCommit,
    qaDataAttribute: 'commit-button',
  };
}

function buildModalActions(
  state: FileImportState,
  onCancel: () => void,
  onCommit: () => void
): Action[] {
  return [buildCancelAction(onCancel), buildCommitAction(state, onCommit)];
}

function getUploadButtonText(state: FileImportState): string {
  switch (state) {
    case FileImportState.FILE_CHOSEN_HAS_ERRORS:
    case FileImportState.COMMIT_HAS_ERRORS:
      return 'Re-upload';
    default:
      return 'Upload';
  }
}
function isFileValid(filePath?: string): boolean {
  return (
    Boolean(filePath) && Boolean(filePath?.endsWith('.xlsx') || Boolean(filePath?.endsWith('.xls')))
  );
}

function groupErrorMessagesByColumn(errors: FileImportError[]): string[] {
  const MAX_ERRORS_PER_COLUMN = 5;
  const NO_COLUMN_NAME = 'undefined';
  const errorMessages: string[] = [];
  const groupedErrors = groupBy(errors, 'columnName');

  Object.keys(groupedErrors).forEach((columnName) => {
    const numErrors = groupedErrors[columnName].length;
    if (numErrors > MAX_ERRORS_PER_COLUMN && columnName !== NO_COLUMN_NAME) {
      errorMessages.push(`${numErrors} rows have an invalid ${columnName}`);
    } else {
      const messagesForColumn = groupedErrors[columnName].map((group) => group.userFacingMessage);
      errorMessages.push(...messagesForColumn);
    }
  });

  return errorMessages;
}

export const ActionItemsImportModal = ({onImport}: {onImport?: () => void}): React.ReactElement => {
  const [state, setState] = React.useState(FileImportState.NEVER_SELECTED_FILE);
  const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
  const [successMessages, setSuccessMessages] = React.useState<string[]>([]);
  const [fileToUpload, setFileToUpload] = React.useState(null);
  const {hideModal} = useModal();

  const clearMessages = React.useCallback(() => {
    setErrorMessages([]);
    setSuccessMessages([]);
  }, []);

  const handleFileSelection = React.useCallback(({filePath, file}) => {
    if (isFileValid(filePath)) {
      clearMessages();
      setFileToUpload(file);
      setState(FileImportState.FILE_CHOSEN_SUCCESSFULLY);
    } else {
      // invalid file
      setState(FileImportState.FILE_CHOSEN_HAS_ERRORS);
      const errorMessages = [];

      if (!isFileValid(filePath)) {
        errorMessages.push(
          'The file type is invalid. It must be an excel file with extension .xlsx or .xls'
        );
      }

      setErrorMessages(errorMessages);
    }
  }, []);

  const handleCommit = React.useCallback(async () => {
    clearMessages();
    if (fileToUpload) {
      let response;
      try {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        response = await dvpApi.actionItemTemplateImport(formData);
      } catch (err) {
        if ((err as any).status === 400) {
          response = (err as any).payload;
        } else {
          throw err;
        }
      }

      if (response.errors) {
        setErrorMessages(groupErrorMessagesByColumn(response.errors));
        setState(FileImportState.COMMIT_HAS_ERRORS);
      } else {
        onImport?.();
        setSuccessMessages([
          `Added ${response.data.actionItemTemplatesAdded} action item record(s)`,
          `Updated ${response.data.actionItemTemplatesUpdated} action item record(s)`,
        ]);
      }
    }
  }, [fileToUpload, onImport]);

  return (
    <Modal
      headerOptions={{title: 'Upload Action Items'}}
      displayType={ModalDisplayType.Small}
      actions={buildModalActions(state, hideModal, handleCommit)}
    >
      <Messages>
        {errorMessages.map((errorMessage) => {
          return (
            <Message key={generatePrefixed('error')} text={errorMessage} type={MessageType.Error} />
          );
        })}
        {successMessages.map((successMessage) => {
          return (
            <Message
              key={generatePrefixed('success')}
              text={successMessage}
              type={MessageType.Success}
            />
          );
        })}
      </Messages>
      <FileInputSection>
        <Paragraph>Choose a file (xls or xlsx only)</Paragraph>
        <FileInputContainer>
          <FileInput
            required
            buttonText={getUploadButtonText(state)}
            onFileChange={handleFileSelection}
            qaAttribute="upload-action-items-bulk"
          />
        </FileInputContainer>
      </FileInputSection>
    </Modal>
  );
};
